@if (integrationStatus === 'Configured' && savedConfiguration) {
  <!-- Configured Status View -->
  <div class="connectwise-configured-container">
    <div class="configured-header">
      <div class="success-icon">
        <i class="k-icon k-i-check-circle k-icon-xxxl"></i>
      </div>
      <h2 class="success-title">Successfully Connected</h2>
      <p class="success-message">Your ConnectWise integration is properly configured and ready to use.</p>
    </div>

    <div class="configuration-details-card">
      <h3>Configuration Details</h3>
      <div class="details-grid">
        <div class="detail-row">
          <span class="detail-label">Username:</span>
          <span class="detail-value">{{ savedConfiguration.username || 'N/A' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Board:</span>
          <span class="detail-value">{{ savedConfiguration.boardName || 'N/A' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">URL:</span>
          <span class="detail-value">{{ savedConfiguration.url || 'N/A' }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Multi-Tenant Support:</span>
          <span class="detail-value">{{ savedConfiguration.supportsMultiTenant ? 'Enabled' : 'Disabled' }}</span>
        </div>
      </div>
      <div class="edit-configuration-container">
        <button
          kendoButton
          themeColor="primary"
          (click)="onEditConfiguration()"
        >
          <kendo-svgicon [icon]="icons?.checkOutline"></kendo-svgicon>
          Edit Configuration
        </button>
      </div>
    </div>
  </div>
} @else {
  <!-- Original Form View -->
  <div class="connectwise-setup-container">
    <div class="setup-header">
      <!-- Configuration Progress Indicator -->
      @if (!isConfigured()) {
        <div class="configuration-progress">
          <div
            class="progress-step"
            [class.completed]="connectionSuccess"
            [class.active]="activeMainTab === TABS.CREDENTIALS"
          >
            <span class="step-number">1</span>
            <span class="step-label">Save Connection</span>
          </div>
          <div
            class="progress-step"
            [class.completed]="hasFieldMappings()"
            [class.active]="activeMainTab === TABS.CONFIGURATION"
          >
            <span class="step-number">2</span>
            <span class="step-label">Configure Field Mappings</span>
          </div>
          @if (isMultiTenantEnabled()) {
            <div
              class="progress-step"
              [class.completed]="hasTenantMappings()"
              [class.active]="activeMainTab === TABS.TENANT_MAPPING"
            >
              <span class="step-number">3</span>
              <span class="step-label">Configure Tenant Mappings</span>
            </div>
          }
        </div>
      }

      <!-- Main tabs navigation -->
      <kendo-tabstrip
        #mainTabStrip
        [keepTabContent]="true"
        (tabSelect)="handleMainTabSelect($event)"
      >
        <kendo-tabstrip-tab
          [title]="'Credentials'"
          [selected]="isMainTabActive(TABS.CREDENTIALS)"
        >
          <ng-template kendoTabContent>
            <div class="setup-content">
              <!-- Credentials Tab Content -->

              <form
                [formGroup]="credentialsForm"
                class="credentials-form"
              >
                <!-- Company ID Field -->
                <div class="form-group">
                  <kendo-label
                    for="companyId"
                    text="Company ID"
                    [optional]="false"
                  ></kendo-label>
                  <kendo-textbox
                    formControlName="companyId"
                    id="companyId"
                    class="w-100"
                    placeholder="Enter your ConnectWise company identifier"
                  ></kendo-textbox>
                  @if (credentialsForm.get('companyId')?.invalid && credentialsForm.get('companyId')?.touched) {
                    <div class="error-message">
                      @if (credentialsForm.get('companyId')?.errors?.['required']) {
                        Company ID is required
                      }
                    </div>
                  }
                </div>

                <!-- Public Key Field -->
                <div class="form-group">
                  <kendo-label
                    for="publicKey"
                    text="Public Key"
                    [optional]="false"
                  ></kendo-label>
                  <kendo-textbox
                    formControlName="publicKey"
                    id="publicKey"
                    class="w-100"
                    placeholder="Enter your ConnectWise public key"
                  ></kendo-textbox>
                  @if (credentialsForm.get('publicKey')?.invalid && credentialsForm.get('publicKey')?.touched) {
                    <div class="error-message">
                      @if (credentialsForm.get('publicKey')?.errors?.['required']) {
                        Public Key is required
                      }
                    </div>
                  }
                </div>

                <!-- Private Key Field -->
                <div class="form-group">
                  <kendo-label
                    for="privateKey"
                    text="Private Key"
                    [optional]="false"
                  ></kendo-label>
                  <kendo-textbox
                    formControlName="privateKey"
                    id="privateKey"
                    type="password"
                    class="w-100"
                    placeholder="Enter your ConnectWise private key"
                  ></kendo-textbox>
                  @if (credentialsForm.get('privateKey')?.invalid && credentialsForm.get('privateKey')?.touched) {
                    <div class="error-message">
                      @if (credentialsForm.get('privateKey')?.errors?.['required']) {
                        Private Key is required
                      }
                    </div>
                  }
                </div>

                <!-- Domain Field -->
                <div class="form-group">
                  <kendo-label
                    for="domain"
                    text="Domain"
                    [optional]="false"
                  ></kendo-label>
                  <kendo-textbox
                    formControlName="domain"
                    id="domain"
                    class="w-100"
                    placeholder="https://example.connectwise.com"
                  ></kendo-textbox>
                  @if (credentialsForm.get('domain')?.invalid && credentialsForm.get('domain')?.touched) {
                    <div class="error-message">
                      @if (credentialsForm.get('domain')?.errors?.['required']) {
                        Domain is required
                      } @else if (credentialsForm.get('domain')?.errors?.['pattern']) {
                        Domain must be a valid URL (http:// or https://)
                      }
                    </div>
                  }
                </div>

                <!-- Multi-tenant support toggle -->
                <div class="form-group">
                  <div class="multi-tenant-toggle">
                    <div class="toggle-header">
                      <span class="toggle-label">
                        Enable Multi-Tenant Support
                        <span class="optional">(Optional)</span>
                      </span>
                    </div>

                    <div class="toggle-control">
                      <input
                        type="checkbox"
                        id="supportsMultiTenant"
                        [checked]="isMultiTenantEnabled()"
                        (change)="toggleMultiTenant($event)"
                        [disabled]="!canBeMultitenant()"
                        class="k-checkbox"
                      />
                      <label
                        class="toggle-switch"
                        for="supportsMultiTenant"
                      ></label>
                    </div>

                    <div class="toggle-description">
                      Enable this to map multiple tenants between Cyflare and ConnectWise
                      @if (isMultiTenantEnabled() && !connectionSuccess) {
                        <br />
                        <small class="text-muted">Save the connection to access tenant mapping</small>
                      }
                    </div>
                  </div>
                </div>
              </form>

              <div class="action-buttons">
                <button
                  kendoButton
                  (click)="cancel.emit()"
                  class="me-2"
                >
                  Cancel
                </button>
                <button
                  kendoButton
                  [primary]="true"
                  [disabled]="loading || credentialsForm.invalid || (connectionTested && !isEditMode)"
                  (click)="onSaveConnection()"
                >
                  @if (loading) {
                    <kendo-loader></kendo-loader>
                  } @else {
                    {{ isEditMode ? 'Update Connection' : 'Save Connection' }}
                  }
                </button>
                @if (connectionSuccess) {
                  <button
                    kendoButton
                    look="outline"
                    class="ms-2"
                    (click)="setMainTab(TABS.CONFIGURATION)"
                  >
                    Continue to Configuration
                  </button>
                }
              </div>
            </div>
          </ng-template>
        </kendo-tabstrip-tab>

        <kendo-tabstrip-tab
          [title]="'Configuration'"
          [selected]="isMainTabActive(TABS.CONFIGURATION)"
          [disabled]="!connectionSuccess"
        >
          <ng-template kendoTabContent>
            <div class="setup-content">
              <!-- Configuration Tab Content -->
              <!-- <div class="instruction-panel">
              <div class="instruction-content">
                <i class="k-icon k-i-info me-2"></i>
                <span>Configure ConnectWise settings and map fields between Cyflare and ConnectWise.</span>
              </div>
            </div> -->

              <!-- Combined Configuration UI -->
              <div class="configuration-container">
                <!-- Base Settings Section -->
                <div class="config-section">
                  <h3>Base Settings</h3>
                  <div class="base-settings-grid">
                    <!-- Board Selection -->
                    <div class="setting-item">
                      <div class="form-group">
                        <kendo-label
                          text="Select Board"
                          [optional]="false"
                        ></kendo-label>

                        @if (connectionSuccess) {
                          <cyflare-autocomplete
                            [baseUrl]="'v1/ticketintegration/administrative/organizations/{orgId}/connectwise_boards/'"
                            [organizationId]="organizationId()"
                            [formGroup]="boardSelectionForm"
                            [formFieldName]="'selectedBoard'"
                            [textField]="'display_text'"
                            [placeholder]="'Search a board...'"
                            [fetchOnInit]="true"
                            [showClear]="true"
                            [showDropdown]="true"
                            [defaultItem]="savedBoardItem()"
                            (itemSelected)="onBoardSelected($event)"
                            (itemCleared)="onBoardCleared()"
                          ></cyflare-autocomplete>
                        }
                      </div>
                    </div>

                    <!-- Company Selection -->
                    <div class="setting-item">
                      <div class="form-group">
                        <kendo-label
                          text="Select Company"
                          [optional]="false"
                        ></kendo-label>

                        @if (connectionSuccess) {
                          <cyflare-autocomplete
                            [baseUrl]="'v1/ticketintegration/administrative/organizations/{orgId}/connectwise_companies/'"
                            [organizationId]="organizationId()"
                            [formGroup]="companySelectionForm"
                            [formFieldName]="'selectedCompany'"
                            [textField]="'display_text'"
                            [placeholder]="'Search a company...'"
                            [fetchOnInit]="true"
                            [showClear]="true"
                            [showDropdown]="true"
                            [defaultItem]="savedCompanyItem()"
                            (itemSelected)="onCompanySelected($event)"
                          ></cyflare-autocomplete>
                        }

                        @if (
                          companySelectionForm.get('selectedCompany')?.invalid &&
                          companySelectionForm.get('selectedCompany')?.touched
                        ) {
                          <div class="error-message">Company selection is required</div>
                        }
                      </div>
                    </div>

                  </div>
                </div>

                <!-- Field Mappings Section -->
                <div class="config-section" [class.disabled-section]="!isBoardSelected()">
                  <h3>Field Mappings</h3>
                  @if (!isBoardSelected()) {
                    <div class="disabled-overlay">
                      <div class="disabled-message">
                        <i class="k-icon k-i-info-circle me-2"></i>
                        Please select a board from Base Settings to configure field mappings.
                      </div>
                    </div>
                  }
                  <!-- Mapping Tabs -->
                  <kendo-tabstrip
                    #mappingTabStrip
                    [keepTabContent]="true"
                    (tabSelect)="handleMappingTabSelect($event)"
                  >
                    <kendo-tabstrip-tab
                      [title]="'Priority Mapping'"
                      [selected]="isMappingTabActive('priority')"
                    >
                      <ng-template kendoTabContent>
                        <!-- Priority Mapping Tab Content -->
                        <div class="field-mappings">
                          <!-- Column Headers -->
                          <div class="mapping-header">
                            <div class="cyflare-field">
                              <span class="header-label">Cyflare Fields</span>
                            </div>
                            <div class="connector">
                              <i class="k-icon k-i-arrow-right"></i>
                            </div>
                            <div class="connectwise-field">
                              <span class="header-label">ConnectWise Fields</span>
                            </div>
                          </div>

                          @for (cyflarePriority of cyflareTicketPriorities; track cyflarePriority.id) {
                            <div class="mapping-row">
                              <div class="cyflare-field">
                                <span class="field-label">
                                  <i
                                    class="k-icon k-i-lock me-1"
                                    title="Fixed Cyflare field"
                                    style="font-size: 0.8rem"
                                  ></i>
                                  {{ cyflarePriority.name }}
                                </span>
                              </div>
                              <div class="connector">
                                <i class="k-icon k-i-arrow-right"></i>
                              </div>
                              <div class="connectwise-field">
                                <kendo-dropdownlist
                                  [data]="connectWisePriorities"
                                  [textField]="'name'"
                                  [valueField]="'id'"
                                  [valuePrimitive]="false"
                                  placeholder="Select ConnectWise Priority"
                                  (valueChange)="updatePriorityMapping(cyflarePriority, $event)"
                                  [value]="getPriorityMappingFor(cyflarePriority.id)"
                                  class="w-100"
                                ></kendo-dropdownlist>
                              </div>
                            </div>
                          }
                        </div>
                      </ng-template>
                    </kendo-tabstrip-tab>

                    <kendo-tabstrip-tab
                      [title]="'Status Mapping'"
                      [selected]="isMappingTabActive('status')"
                    >
                      <ng-template kendoTabContent>
                        <!-- Status Mapping Tab Content -->
                        <div class="field-mappings">
                          <!-- Column Headers -->
                          <div class="mapping-header">
                            <div class="cyflare-field">
                              <span class="header-label">Cyflare Fields</span>
                            </div>
                            <div class="connector">
                              <i class="k-icon k-i-arrow-right"></i>
                            </div>
                            <div class="connectwise-field">
                              <span class="header-label">ConnectWise Fields</span>
                            </div>
                          </div>

                          @for (cyflareStatus of cyflareTicketStatuses; track cyflareStatus.id) {
                            <div class="mapping-row">
                              <div class="cyflare-field">
                                <span class="field-label">
                                  <i
                                    class="k-icon k-i-lock me-1"
                                    title="Fixed Cyflare field"
                                    style="font-size: 0.8rem"
                                  ></i>
                                  {{ cyflareStatus.name }}
                                </span>
                              </div>
                              <div class="connector">
                                <i class="k-icon k-i-arrow-right"></i>
                              </div>
                              <div class="connectwise-field">
                                <kendo-dropdownlist
                                  [data]="connectWiseStatuses"
                                  [textField]="'name'"
                                  [valueField]="'id'"
                                  [valuePrimitive]="false"
                                  placeholder="Select ConnectWise Status"
                                  (valueChange)="updateStatusMapping(cyflareStatus, $event)"
                                  [value]="getStatusMappingFor(cyflareStatus.id)"
                                  class="w-100"
                                ></kendo-dropdownlist>
                              </div>
                            </div>
                          }
                        </div>
                      </ng-template>
                    </kendo-tabstrip-tab>

                    <!-- MF: Temporary gate — remove isCyflareUser check when GA release is ready for production -->
                    @if (isCyflareUser()) {
                      <kendo-tabstrip-tab
                        [title]="'Service Type Mapping'"
                        [selected]="isMappingTabActive('serviceType')"
                      >
                        <ng-template kendoTabContent>
                          <!-- Service Type Mapping Tab Content -->
                          <app-service-type-mapping
                            [organizationId]="organizationId()"
                            [boardId]="savedBoardItem()?.id ?? null"
                            [initialConfig]="savedServiceTypeMappingConfig()"
                            [integrationDetailsPk]="integrationDetailsPk()"
                            (configChanged)="onServiceTypeMappingConfigChanged($event)"
                          ></app-service-type-mapping>
                        </ng-template>
                      </kendo-tabstrip-tab>
                    }
                  </kendo-tabstrip>
                </div>
              </div>

              <div class="action-buttons">
                <button
                  kendoButton
                  (click)="setMainTab(TABS.CREDENTIALS)"
                  class="me-2"
                >
                  Back to Credentials
                </button>
                <button
                  kendoButton
                  [primary]="true"
                  [disabled]="cansavedata()"
                  (click)="onSaveConfiguration()"
                >
                  @if (loading) {
                    <kendo-loader></kendo-loader>
                  } @else {
                    Save Configuration
                  }
                </button>
              </div>
            </div>
          </ng-template>
        </kendo-tabstrip-tab>

        @if (isMultiTenantEnabled() && connectionSuccess && integrationId()) {
          <kendo-tabstrip-tab
            [title]="'Tenant Mapping'"
            [selected]="isMainTabActive(TABS.TENANT_MAPPING)"
            [disabled]="!connectionSuccess || !integrationId()"
          >
            <ng-template kendoTabContent>
              <div class="setup-content tenant-mapping-tab">
                <!-- Tenant Mapping Tab Content -->
                <div class="tenant-mapping-wrapper">
                  <app-tenant-mapping
                    [organizationId]="organizationId()"
                    (close)="setMainTab(TABS.CONFIGURATION)"
                  ></app-tenant-mapping>
                </div>

                <div class="action-buttons">
                  <button
                    kendoButton
                    (click)="setMainTab(TABS.CONFIGURATION)"
                    class="me-2"
                  >
                    Back to Configuration
                  </button>
                  <button
                    kendoButton
                    [primary]="true"
                    [disabled]="loading"
                    (click)="onCompleteTenantMappingSetup()"
                  >
                    @if (loading) {
                      <kendo-loader></kendo-loader>
                    } @else {
                      Complete Setup
                    }
                  </button>
                </div>
              </div>
            </ng-template>
          </kendo-tabstrip-tab>
        }
      </kendo-tabstrip>
    </div>
  </div>
}
